import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { CartItem, CartState } from '../../common/types/CartType';

// Guest cart. Survives a reload through localStorage, and belongs to
// exactly one menu — an order goes to one restaurant, so opening a
// different menu starts a new cart.

const STORAGE_KEY = 'cart';

function readStoredCart(): CartState {
    const empty: CartState = { menuId: null, items: [] };
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return empty;
        const parsed = JSON.parse(raw) as Partial<CartState>;
        if (typeof parsed?.menuId !== 'number' || !Array.isArray(parsed.items)) return empty;
        return { menuId: parsed.menuId, items: parsed.items };
    } catch {
        return empty;
    }
}

function persist(state: CartState): void {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch {
        // Private-mode Safari and full quotas throw here. The cart still
        // works for this session, it just will not survive a reload.
    }
}

const cartSlice = createSlice({
    name: 'cart',
    initialState: readStoredCart(),
    reducers: {
        setCartMenu: (state, action: PayloadAction<number>) => {
            if (state.menuId === action.payload) return;
            state.menuId = action.payload;
            state.items = [];
            persist(state);
        },
        addItem: (state, action: PayloadAction<CartItem>) => {
            const existing = state.items.find((item) => item.dishId === action.payload.dishId);
            if (existing) {
                existing.quantity += action.payload.quantity;
            } else {
                state.items.push(action.payload);
            }
            persist(state);
        },
        removeItem: (state, action: PayloadAction<number>) => {
            state.items = state.items.filter((item) => item.dishId !== action.payload);
            persist(state);
        },
        updateQuantity: (state, action: PayloadAction<{ dishId: number; quantity: number }>) => {
            const item = state.items.find((entry) => entry.dishId === action.payload.dishId);
            if (!item) return;
            if (action.payload.quantity < 1) {
                state.items = state.items.filter((entry) => entry.dishId !== action.payload.dishId);
            } else {
                item.quantity = action.payload.quantity;
            }
            persist(state);
        },
        clearCart: (state) => {
            state.items = [];
            persist(state);
        },
    },
});

export const { setCartMenu, addItem, removeItem, updateQuantity, clearCart } = cartSlice.actions;

export default cartSlice.reducer;
